import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PageHeader } from "../components/Surface";
import { Icon } from "../app/Icon";
import { FormRow, Select, CheckboxGroup, inputClass } from "../components/FormKit";
import { PROFILE_TYPE_LABELS } from "../lib/profiles";
import { LoadPreview } from "../components/LoadPreview";
import { Paywall, usePaywallGate } from "../components/Paywall";
import { AiAssist } from "../components/AiAssist";
import { listMyOrgs, orgCan, type Org } from "../lib/orgs";
import {
  createLoad, LOAD_POST_TYPES,
  CATEGORY_OPTIONS, EQUIPMENT_TYPE_OPTIONS, FULL_PARTIAL_OPTIONS, RATE_TYPE_OPTIONS, SPECIAL_REQUIREMENT_OPTIONS,
} from "../lib/loads";

const DRAFT_KEY = "rt.draft.load";

export function PostLoad() {
  const { active } = useProfiles();
  const [paywall, dismissPaywall] = usePaywallGate();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [postOrg, setPostOrg] = useState("");
  useEffect(() => {
    listMyOrgs().then((r) => setOrgs(r.organizations.filter((o) => orgCan(o.roles, "loads.post")))).catch(() => undefined);
  }, []);
  const navigate = useNavigate();
  const [f, setF] = useState<Record<string, string>>({ category: "freight", rate_type: "flat", full_partial: "full" });
  const [flexible, setFlexible] = useState(false);
  const [requirements, setRequirements] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? "null");
      if (d && d.f) {
        setF(d.f);
        setFlexible(Boolean(d.flexible));
        setRequirements(d.requirements ?? []);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const set = (k: string) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const setE = (k: string) => (e: { target: { value: string } }) => set(k)(e.target.value);
  const num = (v: string) => (v ? Number(v) : undefined);
  const canPost = active ? LOAD_POST_TYPES.includes(active.type) : false;
  const rateHint = f.rate_type === "per_mile" ? "Enter cents per mile (e.g. 210)" : "Enter the total rate in dollars";

  function saveDraft() {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ f, flexible, requirements }));
      setNote("Draft saved on this device.");
    } catch {
      setNote("Could not save the draft.");
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      const { load } = await createLoad({
        poster_profile_id: active.id,
        org_id: postOrg || undefined,
        origin: f.origin,
        destination: f.destination,
        trip_miles: num(f.trip_miles),
        category: f.category || undefined,
        equipment_type: f.equipment_type || undefined,
        full_partial: f.full_partial || undefined,
        commodity: f.commodity || undefined,
        weight: num(f.weight),
        length_ft: num(f.length_ft),
        width_ft: num(f.width_ft),
        height_ft: num(f.height_ft),
        pieces: num(f.pieces),
        temperature: f.temperature || undefined,
        pickup_date: f.pickup_date || undefined,
        pickup_time: f.pickup_time || undefined,
        delivery_date: f.delivery_date || undefined,
        delivery_time: f.delivery_time || undefined,
        flexible_dates: flexible,
        rate_type: f.rate_type || undefined,
        rate: f.rate_type === "negotiable" ? undefined : num(f.rate),
        reference_number: f.reference_number || undefined,
        special_requirements: requirements,
        description: f.description || undefined,
      });
      localStorage.removeItem(DRAFT_KEY);
      navigate(`/loads/${load.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not post the shipment");
    } finally {
      setBusy(false);
    }
  }

  const desc = f.description ?? "";

  return (
    <div className="grid gap-6">
      {paywall && <Paywall onClose={dismissPaywall} />}
      <PageHeader title="Post a shipment" subtitle="Create a new load listing. Carriers bid to haul it." />

      {!canPost ? (
        <p className="text-sm text-muted">Switch to a broker/dispatcher or company profile to post a shipment.</p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
          <form onSubmit={onSubmit}>
          <div className="rounded-card border border-line bg-surface px-5 sm:px-6">
            {orgs.length > 0 && (
              <FormRow label="Post as" hint="Post personally or on behalf of an organization.">
                <select className={inputClass} value={postOrg} onChange={(e) => setPostOrg(e.target.value)} aria-label="Post as">
                  <option value="">Personal ({active?.display_name ?? "me"})</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </FormRow>
            )}
            <FormRow label="Lane" hint="Where does it pick up and deliver?" required>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="relative flex items-center">
                  <Icon name="pin" className="pointer-events-none absolute left-3 h-4 w-4 text-verified" />
                  <input className={`w-full ${inputClass} pl-9`} aria-label="Origin" placeholder="Origin — Dallas, TX" value={f.origin ?? ""} onChange={setE("origin")} required />
                </div>
                <div className="relative flex items-center">
                  <Icon name="pin" className="pointer-events-none absolute left-3 h-4 w-4 text-accent" />
                  <input className={`w-full ${inputClass} pl-9`} aria-label="Destination" placeholder="Destination — Denver, CO" value={f.destination ?? ""} onChange={setE("destination")} required />
                </div>
              </div>
            </FormRow>

            <FormRow label="Distance" hint="Trip distance in miles.">
              <input className={inputClass} type="number" min="0" aria-label="Trip distance" placeholder="780" value={f.trip_miles ?? ""} onChange={setE("trip_miles")} />
            </FormRow>

            <FormRow label="Shipment" hint="Category and load size.">
              <div className="grid gap-3 sm:grid-cols-2">
                <Select label="" value={f.category ?? ""} onChange={set("category")} options={CATEGORY_OPTIONS} placeholder="Category" ariaLabel="Category" />
                <Select label="" value={f.full_partial ?? ""} onChange={set("full_partial")} options={FULL_PARTIAL_OPTIONS} placeholder="Full / partial" ariaLabel="Full or partial" />
              </div>
            </FormRow>

            <FormRow label="Equipment" hint="Trailer / equipment required.">
              <Select label="" value={f.equipment_type ?? ""} onChange={set("equipment_type")} options={EQUIPMENT_TYPE_OPTIONS} placeholder="Equipment" ariaLabel="Equipment" />
            </FormRow>

            <FormRow label="Commodity" hint="What's being shipped?">
              <input className={inputClass} aria-label="Commodity" placeholder="Palletized goods" value={f.commodity ?? ""} onChange={setE("commodity")} />
            </FormRow>

            <FormRow label="Weight & pieces" hint="Weight, count, and reefer temp.">
              <div className="grid gap-3 sm:grid-cols-3">
                <input className={inputClass} type="number" min="0" aria-label="Weight" placeholder="Weight (lbs)" value={f.weight ?? ""} onChange={setE("weight")} />
                <input className={inputClass} type="number" min="0" placeholder="Pieces" value={f.pieces ?? ""} onChange={setE("pieces")} />
                <input className={inputClass} placeholder="Temp (34°F)" value={f.temperature ?? ""} onChange={setE("temperature")} />
              </div>
            </FormRow>

            <FormRow label="Dimensions" hint="Length × width × height (ft).">
              <div className="grid gap-3 sm:grid-cols-3">
                <input className={inputClass} type="number" min="0" placeholder="Length (ft)" value={f.length_ft ?? ""} onChange={setE("length_ft")} />
                <input className={inputClass} type="number" min="0" placeholder="Width (ft)" value={f.width_ft ?? ""} onChange={setE("width_ft")} />
                <input className={inputClass} type="number" min="0" placeholder="Height (ft)" value={f.height_ft ?? ""} onChange={setE("height_ft")} />
              </div>
            </FormRow>

            <FormRow label="Schedule" hint="Pickup and delivery windows.">
              <div className="grid gap-3 sm:grid-cols-2">
                <input className={inputClass} type="date" aria-label="Pickup date" value={f.pickup_date ?? ""} onChange={setE("pickup_date")} />
                <input className={inputClass} aria-label="Pickup time" placeholder="Pickup time / Appt" value={f.pickup_time ?? ""} onChange={setE("pickup_time")} />
                <input className={inputClass} type="date" aria-label="Delivery date" value={f.delivery_date ?? ""} onChange={setE("delivery_date")} />
                <input className={inputClass} aria-label="Delivery time" placeholder="Delivery time / Appt" value={f.delivery_time ?? ""} onChange={setE("delivery_time")} />
              </div>
              <label className="flex items-center gap-2 text-xs font-bold text-muted">
                <input type="checkbox" checked={flexible} onChange={(e) => setFlexible(e.target.checked)} /> Dates are flexible
              </label>
            </FormRow>

            <FormRow label="Rate" hint="How the rate is quoted." required>
              <Select label="" value={f.rate_type ?? ""} onChange={set("rate_type")} options={RATE_TYPE_OPTIONS} ariaLabel="Rate type" />
              {f.rate_type !== "negotiable" && (
                <>
                  <input className={inputClass} type="number" min="0" aria-label="Rate" placeholder="Rate" value={f.rate ?? ""} onChange={setE("rate")} />
                  <span className="font-mono text-[11px] text-muted">{rateHint}</span>
                </>
              )}
            </FormRow>

            <FormRow label="Requirements" hint="Special handling / equipment.">
              <CheckboxGroup label="" options={SPECIAL_REQUIREMENT_OPTIONS} selected={requirements} onToggle={(v) => setRequirements((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]))} />
            </FormRow>

            <FormRow label="Reference" hint="Your load / reference number.">
              <input className={inputClass} placeholder="GC-10241" value={f.reference_number ?? ""} onChange={setE("reference_number")} />
            </FormRow>

            <FormRow label="Notes" hint="Loading details, contact preferences.">
              <textarea className={inputClass} rows={5} maxLength={2000} placeholder="Drop-and-hook available, appointment required…" value={desc} onChange={setE("description")} />
              <div className="flex items-center justify-between gap-2">
                <AiAssist kind="load_description" value={desc} onResult={(t) => set("description")(t.slice(0, 2000))} label="Draft with AI" />
                <span className="font-mono text-[11px] text-muted">{desc.length}/2000</span>
              </div>
            </FormRow>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t-2 border-dashed border-accent/40 py-5">
              <p className="flex flex-wrap items-center gap-2 text-sm text-muted">
                <Icon name="verify" className="h-4 w-4 text-verified" />
                Publishing requires your {active ? PROFILE_TYPE_LABELS[active.type] : "company"} profile to be verified.{" "}
                <Link to="/verify" className="font-bold text-accent-strong">Verify now →</Link>
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {error && <span className="font-mono text-sm text-danger">{error}</span>}
                {note && <span className="font-mono text-sm text-muted">{note}</span>}
                <Button variant="ghost" type="button" onClick={saveDraft}>Save draft</Button>
                <Button type="submit" disabled={busy || !f.origin || !f.destination}>{busy ? "Publishing…" : "Publish"}</Button>
              </div>
            </div>
          </div>

          <Link to="/loads" className="mt-3 inline-block text-sm font-bold text-muted">Cancel</Link>
          </form>
          <LoadPreview f={f} requirements={requirements} name={active?.display_name ?? undefined} />
        </div>
      )}
    </div>
  );
}
